import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../redux/store";
import { Row, Col, Container } from "react-bootstrap";

import { setSelectedProduct } from "../redux/slices/productDetailSlice";
import { BioBadgeStyle, StyledButton, VeganBadgeStyle, VegetarianBadgeStyle } from "../components";
import { useTranslation } from "react-i18next";
import { useWishlist, useCart } from "../hooks";
import { fetchProductById } from "../utils/api/apiNoAuth";

const ProductDetails: React.FC = () => {
  const { t } = useTranslation(["productPage", "common"]);
  const { toggle, isInWishlist } = useWishlist();
  const { addItemToCart } = useCart();
  const [quantity, setQuantity] = useState(1);
  const product = useSelector((state: RootState) => state.productDetail.selectedProduct);

  if (!product) return null;

  const sectionStyle: React.CSSProperties = {
    marginBottom: "20px",
  };

  const headlineStyle: React.CSSProperties = {
    fontWeight: 700,
    fontSize: "1.5rem",
    lineHeight: "24px",
    letterSpacing: ".25em",
    display: "block",
    padding: 0,
    marginBottom: "10px",
    textTransform: "uppercase",
    color: "#c39d63",
    fontFamily: "Avenir Next Codensed Ultralight",
  };

  const contentStyle: React.CSSProperties = {
    fontFamily: "Avenir Next Codensed Regular",
    fontWeight: 300,
    fontSize: "20px",
    lineHeight: "24px",
    textTransform: "capitalize",
    color: "#342714",
    marginTop: "1.5rem",
  };

  const subheadlineStyle: React.CSSProperties = {
    fontFamily: "Avenir Next Codensed Ultralight",
    fontStyle: "normal",
    fontWeight: 300,
    fontSize: "64px",
    lineHeight: "62px",
    letterSpacing: ".03em",
    textTransform: "capitalize",
    color: "#514633",
    display: "block",
    marginBottom: "65px",
    wordWrap: "break-word",
  };

  const selectStyle: React.CSSProperties = {
    border: "1px solid #d3d7da",
    borderRadius: "4px",
    fontFamily: "Avenir Next Codensed Regular",
    fontSize: "15px",
    fontWeight: 300,
    backgroundColor: "#fff",
    color: "rgb(52, 39, 20)",
    minHeight: 50,
    maxHeight: 60,
    padding: "20px 30px",
    marginTop: 40,
    marginRight: 20,
    lineHeight: 18,
    transition: "0.3s"
  };

  const priceStyle: React.CSSProperties = {
    fontFamily: "Avenir Next Codensed Ultralight",
    fontSize: "2.4rem",
    fontWeight: 600,
    color: "#c39d63",
  };

  const heartStyle: React.CSSProperties = {
    width: "64px",
    height: "64px",
    cursor: "pointer",
    fill: isInWishlist(product.productId) ? "#c39d63" : "transparent",
    stroke: "#c39d63",
    strokeWidth: 0.5,
    transition: "all 0.3s ease",
    verticalAlign: "bottom",
  };

  const handleOnChangeQuantity = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setQuantity(Number(e.target.value));
  }

  return (
    <Container>
      <section style={{ marginBottom: "100px" }} id={"section_intro"}>
        <Row>
          <Col lg={7} sm={12}>
            <img
              //src={`/products/${product.sku}.jpg`}
              src={'https://bhmm.de/api/v1/images/products/7cd7f82c16de05b7a29b4079b57f24a3'}
              alt={product.name}
              style={{ width: "100%", borderRadius: "15px", marginBottom: "20px", objectFit: "cover" }}
            />
          </Col>

          <Col lg={5} sm={12} style={{ position: "sticky", top: 0, alignSelf: "flex-start" }}>
            <div style={{ ...sectionStyle }}>
              <div style={subheadlineStyle}>{product.name}
                <span style={{ paddingLeft: 15 }}>
                  <svg
                    onClick={() => toggle(product.productId)}
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    style={heartStyle}
                  >
                    <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 
                      2 5.42 4.42 3 7.5 3c1.74 0 3.41 0.81 
                      4.5 2.09C13.09 3.81 14.76 3 16.5 3 
                      19.58 3 22 5.42 22 8.5c0 3.78-3.4 
                      6.86-8.55 11.54L12 21.35z" />
                  </svg>
                </span>
              </div>
            </div>

            <div style={{ ...sectionStyle }}>
              <div style={priceStyle}>{`${product.currency} ${product.price}`}</div>
              <span>{t("product.price_note", { tax: product.taxPercentage })}</span>
            </div>

            <div style={{...sectionStyle}}>
              <select
                style={selectStyle}
                value={quantity}
                onChange={(e) => (handleOnChangeQuantity(e))}
              >
                {product.quantityOptions?.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <StyledButton title={t("product.add_to_cart")} onClick={() => addItemToCart(product.productId, quantity)} isRevert={true} />
            </div>

            <div style={sectionStyle}>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                {product.isBiological && <span style={BioBadgeStyle}>{t("product.badges.bio")}</span>}
                {product.isVegan && <span style={VeganBadgeStyle}>{t("product.badges.vegan")}</span>}
                {product.isVegetarian && <span style={VegetarianBadgeStyle}>{t("product.badges.vegetarian")}</span>}
              </div>
            </div>
          </Col>
        </Row>

        <Row>
          <Col lg={7} sm={12}>
            <div style={sectionStyle}>
              <p style={contentStyle}>{product.description}</p>
            </div>

            {product.ingredients && (
              <div style={sectionStyle}>
                <h4 style={headlineStyle}>{t("product.ingredients.title")}</h4>
                <p style={contentStyle}>{product.ingredients}</p>
              </div>
            )}

            {product.allergenes && (
              <div style={sectionStyle}>
                <h4 style={headlineStyle}>{t("product.allergenes.title")}</h4>
                <p style={contentStyle}>{product.allergenes}</p>
              </div>
            )}
          </Col>

          <Col lg={5} sm={12}>
            <div style={sectionStyle}>
              <h4 style={{ ...headlineStyle }}>{t("product.nutrition.title")}</h4>
              <p style={contentStyle}>{t("product.nutrition.header")}</p>
              {product.nutrition?.map((n) => (
                <div key={n.name} style={{
                  ...contentStyle,
                  display: "flex",
                  justifyContent: "space-between",
                  borderBottom: "1px solid #ccc",
                  paddingBottom: "6px",
                  marginTop: '1rem'
                }}>
                  <span>{n.name}</span>
                  <span>{n.value}</span>
                </div>
              ))}
            </div>
          </Col>
        </Row>
      </section>
    </Container>
  );
};

const ProductPage: React.FC = () => {
  const { productId } = useParams<{ productId: string }>();
  const dispatch = useDispatch();

  const productList = useSelector((state: RootState) => state.productList.products);

  useEffect(() => {
    const loadProduct = async () => {
      if (!productId) return;

      const productFromStore = productList.find(p => p.productId === productId);
      if (productFromStore) {
        dispatch(setSelectedProduct(productFromStore));
      } else {
        try {
          const response = await fetchProductById(productId);
          dispatch(setSelectedProduct(response.data));
        } catch (error) {
          console.error("Produkt konnte nicht geladen werden", error);
        }
      }
    };
    loadProduct();
  }, [productId, productList, dispatch]);

  return <ProductDetails />;
};

export default ProductPage;
